import React, { useEffect, useCallback, Fragment, useState, useRef } from "react";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SaveIcon from "@mui/icons-material/Save";
import { useOutletContext, useParams } from "react-router-dom";
import AllMainTreatmentTr from "./AllMainTreatmentTr";
import baseUrl from "../../../../../api/baseUrl";
import TreatmentFormModal from "../treatmentform/TreatmentFormModal";
import CaseTreatmentSection from "./CaseTreatmentSection";
import DateWiseTreatments from "./DateWiseTreatments";
import {
  isTreatmentItemEditable,
  isTreatmentItemPaid,
} from "./treatmentItemUtils";
import { type TreatmentFormData } from "../treatmentform/treatmentFormUtils";
import { toast } from "react-toastify";
import getDecryptedToken from "../../../../../api/getDecryptedToken";
import type { AddTreatmentOutletContext } from "../../../addTreatmentOutletContext";
import { DEFAULT_MAIN_TREATMENT_STATUS } from "../../../treatmentDrafts/saveVisitDrafts";

interface TreatmentsProps {
  allTeatment: any[];
  setallTeatment: (value: any) => void;
  fetchData: () => void;
  registerSavePending?: (saveFn: (() => Promise<boolean>) | null) => void;
  registerHasPending?: (pending: boolean) => void;
  showDateWise?: boolean;
}

const Treatments: React.FC<TreatmentsProps> = ({
  allTeatment,
  setallTeatment,
  fetchData,
  registerSavePending,
  registerHasPending,
  showDateWise = false,
}) => {
  const { ID } = useParams<{ ID: string }>();
  const { setHasPendingInput, drafts } =
    useOutletContext<AddTreatmentOutletContext>() ?? {};
  // Unsaved new rows live in the shell's draft store so they survive a tab
  // switch; only inline edits of saved rows stay local (and block switching).
  const draftRows = drafts?.drafts.treatments ?? [];
  const isSavingDrafts = drafts?.isSaving ?? false;
  const savedAt = drafts?.savedAt ?? 0;
  const [editingTreatmentId, setEditingTreatmentId] = useState<string | null>(
    null
  );
  const [openSubTreatmentId, setOpenSubTreatmentId] = useState<string | null>(
    null
  );

  useEffect(() => {
    const editing = !!editingTreatmentId || !!openSubTreatmentId;
    setHasPendingInput?.(editing);
    // Payment still waits for unsaved new rows as well.
    registerHasPending?.(editing || draftRows.length > 0);
    return () => {
      setHasPendingInput?.(false);
      registerHasPending?.(false);
    };
  }, [
    draftRows.length,
    editingTreatmentId,
    openSubTreatmentId,
    setHasPendingInput,
    registerHasPending,
  ]);

  const editingTreatment = allTeatment?.find(
    (item: any) => String(item._id) === String(editingTreatmentId)
  );

  const getData = useCallback(
    (id: string) => {
      baseUrl
        .get("treatment/find-treatment", {
          headers: { treatmentid: id },
        })
        .then((response) => {
          setallTeatment(response.data.data.treatment);
        })
        .catch((error) => {
          console.error("Error fetching treatment data:", error);
        });
    },
    [setallTeatment]
  );

  useEffect(() => {
    if (ID) {
      getData(ID);
    }
  }, [ID, getData]);

  // Save All from the bottom bar ran while this tab was open: show the rows.
  const lastSavedAtRef = useRef(savedAt);
  useEffect(() => {
    if (savedAt === lastSavedAtRef.current) return;
    lastSavedAtRef.current = savedAt;
    if (ID) getData(ID);
    fetchData();
  }, [savedAt, ID, getData, fetchData]);

  const removediagnose = useCallback(
    (e: any) => {
      if (e._id) {
        setallTeatment((prevTreatments: any) =>
          prevTreatments.filter((treatment: any) => treatment._id !== e._id)
        );

        baseUrl
          .post(
            "treatment/remove-treatments",
            {},
            {
              headers: {
                treatmentid: ID,
                treatmentsid: e._id,
              },
            }
          )
          .then(() => {
          })
          .catch((error) => {
            console.error("Error removing treatment:", error);
          });
      } else {
        setallTeatment((prevTreatments: any) =>
          prevTreatments.filter((treatment: any) => treatment.tid !== e.tid)
        );
      }
    },
    [ID, setallTeatment]
  );

  const refresh = useCallback(() => {
    if (ID) {
      getData(ID);
    }
  }, [ID, getData]);

  const handleEditTreatment = useCallback((item: any) => {
    if (!item?._id) {
      toast.error("Please refresh and try again");
      refresh();
      return;
    }
    const isSuperAdmin = getDecryptedToken("role") === "Super Admin";
    if (isTreatmentItemPaid(item) && !isSuperAdmin) {
      toast.error("Paid treatment cannot be edited");
      return;
    }
    if (!isTreatmentItemEditable(item, isSuperAdmin)) return;
    setOpenSubTreatmentId(null);
    setEditingTreatmentId(String(item._id));
  }, [refresh]);

  const closeEditForm = useCallback(() => {
    setEditingTreatmentId(null);
  }, []);

  const handleDraftChange = useCallback(
    (draftId: string, formData: TreatmentFormData) => {
      drafts?.updateTreatmentDraft(draftId, formData);
    },
    [drafts]
  );

  const handleDraftRemove = useCallback(
    (draftId: string) => {
      drafts?.removeTreatmentDraft(draftId);
    },
    [drafts]
  );

  const addDraftForm = useCallback(() => {
    setEditingTreatmentId(null);
    setOpenSubTreatmentId(null);
    drafts?.addTreatmentDraft();
  }, [drafts]);

  const saveAllPendingTreatments = useCallback(async (): Promise<boolean> => {
    if (!ID || draftRows.length === 0) return true;
    if (!drafts) return false;
    return drafts.saveKind("treatments");
  }, [ID, draftRows.length, drafts]);

  useEffect(() => {
    registerSavePending?.(saveAllPendingTreatments);
    return () => registerSavePending?.(null);
  }, [registerSavePending, saveAllPendingTreatments]);

  if (showDateWise) {
    return (
      <DateWiseTreatments
        allTeatment={allTeatment}
        setallTeatment={setallTeatment}
        fetchData={fetchData}
        refresh={refresh}
        editingTreatmentId={editingTreatmentId}
        editingTreatment={editingTreatment}
        closeEditForm={closeEditForm}
        handleEditTreatment={handleEditTreatment}
        removediagnose={removediagnose}
        openSubTreatmentId={openSubTreatmentId}
        setOpenSubTreatmentId={setOpenSubTreatmentId}
        setEditingTreatmentId={setEditingTreatmentId}
      />
    );
  }

  return (
    <table className="w-full">
      <thead>
        <tr className="bg-amber-50/80 dark:bg-amber-900/30">
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Date</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Treatment</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Tooth No</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Qty</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Price</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Status</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Dentist</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Note</th>
          <th className="px-4 py-3 text-left text-xs font-semibold text-amber-800 dark:text-amber-400 uppercase tracking-wider">Action</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
        {allTeatment?.map((item: any, index: number) => (
          <Fragment key={item._id ?? index}>
            {String(editingTreatmentId) === String(item._id) && editingTreatment ? (
              <TreatmentFormModal
                key={String(item._id)}
                inlineRow
                defaultStatus={DEFAULT_MAIN_TREATMENT_STATUS}
                treatment={allTeatment}
                setaddTreatmentOpent={closeEditForm}
                settreatment={setallTeatment}
                fetchData={fetchData}
                editItem={editingTreatment}
                onClose={closeEditForm}
              />
            ) : (
              <AllMainTreatmentTr
                item={item}
                handleEditTreatment={handleEditTreatment}
                onClick={() => removediagnose(item)}
                refresh={refresh}
                onAddSubTreatment={() => {
                  setEditingTreatmentId(null);
                  setOpenSubTreatmentId((prev) =>
                    prev === item._id ? null : item._id
                  );
                }}
                isSubFormOpen={openSubTreatmentId === item._id}
              />
            )}
            <CaseTreatmentSection
              item={item}
              allTeatment={allTeatment}
              setallTeatment={setallTeatment}
              refresh={refresh}
              showAddForm={openSubTreatmentId === item._id}
              onCloseAddForm={() => setOpenSubTreatmentId(null)}
            />
          </Fragment>
        ))}
        {draftRows.map((row) => (
          <TreatmentFormModal
            key={row.id}
            inlineRow
            deferSave
            draftId={row.id}
            initialDraft={row.data}
            draftError={row.error}
            defaultStatus={DEFAULT_MAIN_TREATMENT_STATUS}
            treatment={allTeatment}
            setaddTreatmentOpent={() => handleDraftRemove(row.id)}
            settreatment={setallTeatment}
            fetchData={fetchData}
            onClose={() => handleDraftRemove(row.id)}
            onDraftChange={handleDraftChange}
            onDraftRemove={handleDraftRemove}
          />
        ))}
        <tr className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
          <td colSpan={9} className="px-4 py-3">
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={addDraftForm}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border border-dashed border-indigo-300 dark:border-primary/50 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-primary/10 text-xs font-medium transition-colors"
              >
                <AddCircleOutlineIcon sx={{ fontSize: 18 }} />
                Add Treatment
              </button>
              {draftRows.length > 0 && (
                <button
                  type="button"
                  onClick={() => void saveAllPendingTreatments()}
                  disabled={isSavingDrafts}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-medium text-xs hover:shadow-lg hover:shadow-primary/20 transition-all disabled:opacity-50"
                >
                  <SaveIcon sx={{ fontSize: 16 }} />
                  {isSavingDrafts
                    ? "Saving..."
                    : `Save Treatments (${draftRows.length})`}
                </button>
              )}
            </div>
          </td>
        </tr>
      </tbody>
    </table>
  );
};

export default Treatments;
